import 'pages/external-agent-scheduling/registerRoutes';
import React from 'react';
import { registerExternalAgentInterviewTransport } from 'pages/external-agent-chat/interviewTransport';
import type { CandidateRouteKey } from 'candidate-url-registry';

import {
  loadAlternativeRole,
  loadWebCall,
  loadFeedback,
  loadFeedbackSuccess,
  loadHMIntake,
  loadInterviewCompleted,
  loadInterviewRejoin,
  loadInterviewScheduling,
  loadOptOutSuccess,
  loadPracticeCompletion,
  loadPreapplyOptOut,
  loadRescheduleInterview,
  loadRescheduleReason,
  loadResumeFeedbackDetails,
  loadReviewAction,
  loadScreenSharePermissionHelp,
  loadUninterested,
  loadVideoPermissionHelp,
  loadWebCallError,
  loadWithdrawal,
} from './routeChunks';
import type { CandidateMount } from './types';

registerExternalAgentInterviewTransport();

// Every page is loaded for its own route, including the interview runtime.
// createCandidateRouter runs the lazy loaders (routeChunks.ts) through
// lazyWithReload, which auto-recovers from deploy-skew chunk failures.

export const candidateRouteMounts: Record<CandidateRouteKey, CandidateMount> = {
  WEB_INTERVIEW_SESSION: {
    kind: 'lazy',
    load: loadWebCall,
    wrapper: 'route',
  },
  WEB_INTERVIEW_SESSION_REJOIN: {
    kind: 'lazy',
    load: loadInterviewRejoin,
    wrapper: 'route',
  },
  WEB_INTERVIEW_SESSION_SCHEDULE: {
    kind: 'lazy',
    load: loadInterviewScheduling,
    wrapper: 'route',
  },
  WEB_INTERVIEW_SESSION_SCHEDULE_CONFIRM: {
    kind: 'lazy',
    load: loadInterviewScheduling,
    wrapper: 'route',
  },
  WEB_INTERVIEW_SESSION_SCHEDULE_SCHEDULED: {
    kind: 'lazy',
    load: loadInterviewScheduling,
    wrapper: 'route',
  },
  FEEDBACK_SESSION: { kind: 'lazy', load: loadFeedback, wrapper: 'route' },
  FEEDBACK_SESSION_SUCCESS: {
    kind: 'lazy',
    load: loadFeedbackSuccess,
    wrapper: 'route',
  },
  WEB_INTERVIEW_TEST_CALL_REJOIN: {
    kind: 'lazy',
    load: loadInterviewRejoin,
    wrapper: 'route',
  },
  WEB_INTERVIEW_TEST_CALL: {
    kind: 'lazy',
    load: loadWebCall,
    wrapper: 'route',
  },
  WEB_INTERVIEW_PRACTICE: {
    kind: 'lazy',
    load: loadWebCall,
    wrapper: 'route',
  },
  WEB_INTERVIEW_REJOIN: {
    kind: 'lazy',
    load: loadInterviewRejoin,
    wrapper: 'route',
  },
  WEB_INTERVIEW: { kind: 'lazy', load: loadWebCall, wrapper: 'route' },
  WEB_INTERVIEW_SCHEDULE: {
    kind: 'lazy',
    load: loadInterviewScheduling,
    wrapper: 'route',
  },
  WEB_INTERVIEW_SCHEDULE_CONFIRM: {
    kind: 'lazy',
    load: loadInterviewScheduling,
    wrapper: 'route',
  },
  WEB_INTERVIEW_SCHEDULE_SCHEDULED: {
    kind: 'lazy',
    load: loadInterviewScheduling,
    wrapper: 'route',
  },

  WEB_CALL_LEGACY: {
    kind: 'lazy',
    load: loadWebCall,
    wrapper: 'route',
  },
  WEB_CALL_LEGACY_SCHEDULE: {
    kind: 'lazy',
    load: loadInterviewScheduling,
    wrapper: 'route',
  },
  WEB_CALL_LEGACY_SCHEDULE_CONFIRM: {
    kind: 'lazy',
    load: loadInterviewScheduling,
    wrapper: 'route',
  },
  WEB_CALL_LEGACY_SCHEDULE_SCHEDULED: {
    kind: 'lazy',
    load: loadInterviewScheduling,
    wrapper: 'route',
  },
  WEB_CALL_LEGACY_FEEDBACK: {
    kind: 'lazy',
    load: loadFeedback,
    wrapper: 'route',
  },

  RESCHEDULE: {
    kind: 'lazy',
    load: loadRescheduleReason,
    wrapper: 'route',
  },
  RESCHEDULE_CALENDAR: {
    kind: 'lazy',
    load: loadRescheduleInterview,
    wrapper: 'route',
  },

  WEB_INTERVIEW_ERROR: {
    kind: 'lazy',
    load: loadWebCallError,
    wrapper: 'route',
  },
  WEB_INTERVIEW_COMPLETED: {
    kind: 'lazy',
    load: loadInterviewCompleted,
    wrapper: 'route',
  },
  WEB_INTERVIEW_VIDEO_HELP: {
    kind: 'lazy',
    load: loadVideoPermissionHelp,
    wrapper: 'none',
  },
  WEB_INTERVIEW_SCREEN_SHARE_HELP: {
    kind: 'lazy',
    load: loadScreenSharePermissionHelp,
    wrapper: 'none',
  },

  FEEDBACK_SUCCESS: {
    kind: 'lazy',
    load: loadFeedbackSuccess,
    wrapper: 'route',
  },
  FEEDBACK_TEST_CALL_SUCCESS: {
    kind: 'lazy',
    load: loadFeedbackSuccess,
    wrapper: 'route',
  },
  FEEDBACK: { kind: 'lazy', load: loadFeedback, wrapper: 'route' },
  FEEDBACK_TEST_CALL: { kind: 'lazy', load: loadFeedback, wrapper: 'route' },

  OPT_OUT_SUCCESS: {
    kind: 'lazy',
    load: loadOptOutSuccess,
    wrapper: 'route',
  },
  ACCOMMODATION_REQUEST_SUCCESS: {
    kind: 'lazy',
    // OptOutSuccessPage renders the accommodation copy from a prop, so the
    // loader binds it rather than the route declaring a second page.
    load: () =>
      loadOptOutSuccess().then((m) => ({
        default: () => <m.default type='accommodation' />,
      })),
    wrapper: 'route',
  },

  PRACTICE_COMPLETED: {
    kind: 'lazy',
    load: loadPracticeCompletion,
    wrapper: 'route',
  },

  REJECTION_REVIEW: {
    kind: 'lazy',
    load: loadReviewAction,
    wrapper: 'none',
  },
  CANDIDATE_RESUME_FEEDBACK_DETAILS: {
    kind: 'lazy',
    load: loadResumeFeedbackDetails,
    wrapper: 'none',
  },
  CANDIDATE_WITHDRAW: {
    kind: 'lazy',
    load: loadWithdrawal,
    wrapper: 'none',
  },
  CANDIDATE_UNINTERESTED: {
    kind: 'lazy',
    load: loadUninterested,
    wrapper: 'none',
  },
  CANDIDATE_ALTERNATIVE_ROLE: {
    kind: 'lazy',
    load: loadAlternativeRole,
    wrapper: 'none',
  },
  CANDIDATE_PREAPPLY_OPT_OUT: {
    kind: 'lazy',
    load: loadPreapplyOptOut,
    wrapper: 'none',
  },

  HM_INTAKE: {
    kind: 'lazy',
    load: loadHMIntake,
    wrapper: 'none',
  },
};
